import React, { useState, useEffect, useCallback } from 'react';
import { usageReportsAPI, vansAPI, usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Loader2, Package, Truck, UserCircle, DollarSign, Filter, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

function today() {
  return new Date().toISOString().split('T')[0];
}
function thirtyDaysAgo() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
}

export default function UsageReport() {
  const { hasEntitlement } = useAuth();
  const hasCrm = hasEntitlement('crm');

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [vehicleId, setVehicleId] = useState('');
  const [userId, setUserId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load filter options
  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, uRes] = await Promise.all([
          vansAPI.list({ is_active: true }),
          usersAPI.list({ include_archived: false }),
        ]);
        setVehicles(vRes.data?.vans || vRes.data || []);
        setUsers(uRes.data?.users || uRes.data || []);
      } catch { /* non-critical */ }
    };
    load();
  }, []);

  const fetchReport = useCallback(async () => {
    if (!dateFrom || !dateTo) { toast.error('Select a date range'); return; }
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (vehicleId) params.vehicle_id = vehicleId;
      if (userId) params.user_id = userId;
      const res = await usageReportsAPI.usage(params);
      setReport(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'ENTITLEMENT_REQUIRED') {
        toast.error('CRM entitlement required for usage reports');
      } else {
        toast.error('Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, vehicleId, userId]);

  // Auto-fetch on mount
  useEffect(() => { fetchReport(); }, []);

  if (!hasCrm) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <p>CRM entitlement required for usage reports.</p>
        </div>
      </Layout>
    );
  }

  const totalItemsCost = report?.top_items?.reduce((s, i) => s + i.net_cost, 0) || 0;
  const totalItemsQty = report?.top_items?.reduce((s, i) => s + i.net_qty, 0) || 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="usage-report-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="usage-report-title">
            <BarChart3 className="w-6 h-6" /> Inventory Usage Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Materials consumed by jobs — net of reversals</p>
        </div>

        {/* Filters */}
        <Card data-testid="usage-filters">
          <CardContent className="pt-4">
            <div className="space-y-3">
              {/* Row 1: Date range — 2-col grid */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full" data-testid="filter-date-from" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full" data-testid="filter-date-to" />
                </div>
              </div>
              {/* Row 2: Vehicle + Technician */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Vehicle</Label>
                  <Select value={vehicleId || 'all'} onValueChange={(v) => setVehicleId(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-full" data-testid="filter-vehicle"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vehicles</SelectItem>
                      {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Technician</Label>
                  <Select value={userId || 'all'} onValueChange={(v) => setUserId(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-full" data-testid="filter-tech"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Techs</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Apply button */}
              <Button onClick={fetchReport} disabled={loading} className="w-full sm:w-auto" data-testid="apply-filters-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading && (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        )}

        {!loading && report && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SummaryCard icon={Package} label="Total Items Used" value={totalItemsQty} sub={`${report.top_items?.length || 0} unique items`} testId="summary-total-qty" />
              <SummaryCard icon={DollarSign} label="Total Parts Cost" value={`$${totalItemsCost.toFixed(2)}`} sub="Net of reversals" testId="summary-total-cost" />
              <SummaryCard icon={Truck} label="Vehicles Active" value={report.by_vehicle?.length || 0} sub={`${report.by_tech?.length || 0} technicians`} testId="summary-vehicles" />
            </div>

            {/* Top Items */}
            <Card data-testid="top-items-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="w-4 h-4" /> Top Items Used (Qty)</CardTitle>
              </CardHeader>
              <CardContent>
                {report.top_items?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="pb-2 pr-4">Item</th>
                          <th className="pb-2 pr-4">SKU</th>
                          <th className="pb-2 pr-4 text-right">Net Qty</th>
                          <th className="pb-2 text-right">Net Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.top_items.map((item, idx) => (
                          <tr key={item.item_id} className="border-b last:border-0" data-testid={`usage-item-row-${idx}`}>
                            <td className="py-2 pr-4 font-medium">{item.item_name}</td>
                            <td className="py-2 pr-4 text-muted-foreground">{item.item_sku || '—'}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{item.net_qty}</td>
                            <td className="py-2 text-right tabular-nums">${item.net_cost.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No items used in this period.</p>
                )}
              </CardContent>
            </Card>

            {/* By Vehicle + By Tech side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card data-testid="by-vehicle-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Truck className="w-4 h-4" /> Usage by Vehicle</CardTitle>
                </CardHeader>
                <CardContent>
                  {report.by_vehicle?.length > 0 ? (
                    <div className="space-y-2">
                      {report.by_vehicle.map((v) => (
                        <div key={v.vehicle_id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" data-testid={`vehicle-row-${v.vehicle_id}`}>
                          <span className="font-medium">{v.vehicle_name}</span>
                          <div className="flex items-center gap-4 text-muted-foreground tabular-nums">
                            <span>{v.net_qty} items</span>
                            <span className="font-medium text-foreground">${v.net_cost.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">No vehicle usage.</p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="by-tech-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><UserCircle className="w-4 h-4" /> Usage by Technician</CardTitle>
                </CardHeader>
                <CardContent>
                  {report.by_tech?.length > 0 ? (
                    <div className="space-y-2">
                      {report.by_tech.map((t) => (
                        <div key={t.user_id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" data-testid={`tech-row-${t.user_id}`}>
                          <span className="font-medium">{t.user_name}</span>
                          <div className="flex items-center gap-4 text-muted-foreground tabular-nums">
                            <span>{t.net_qty} items</span>
                            <span className="font-medium text-foreground">${t.net_cost.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4 text-center">No tech usage.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold tracking-tight">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
